import { defineStore } from 'pinia';
import type { GeoCoordinates } from '@/views/search/types.ts';

export type AnonymousUserId = string & { readonly __brand: unique symbol };

export interface CookieConsent {
  analytics: boolean;
  marketing: boolean;
}

type LegacyCookieConsent = 'accepted' | 'rejected';
type PersistedCookieConsent = LegacyCookieConsent | CookieConsent | null;

export const useUserStore = defineStore('user', {
  state: () => ({
    _uuid: null as AnonymousUserId | null,
    _cookieConsent: null as PersistedCookieConsent,
    _coordinates: undefined as GeoCoordinates | undefined,
  }),

  getters: {
    uuid: (state) => {
      if (!state._uuid || !isValidAnonymousUserId(state._uuid)) {
        return null;
      }
      return state._uuid;
    },
    cookieConsent: (state): CookieConsent | null => normalizeCookieConsent(state._cookieConsent),
    hasCookieConsent(): boolean {
      return this.cookieConsent !== null;
    },
    analyticsConsent(): boolean {
      return true;
    },
    marketingConsent(): boolean {
      return this.cookieConsent?.marketing === true;
    },
    coordinates: (state) => state._coordinates,
  },

  actions: {
    newUuid() {
      const uuid = crypto.randomUUID() as AnonymousUserId;
      this._uuid = uuid;
      return uuid;
    },
    acceptCookies() {
      this._cookieConsent = { analytics: true, marketing: true };
    },
    rejectCookies() {
      this._cookieConsent = { analytics: false, marketing: false };
    },
    setCookieConsent(consent: CookieConsent) {
      this._cookieConsent = { analytics: consent.analytics, marketing: consent.marketing };
    },
    setCoordinates(coordinates: GeoCoordinates | undefined) {
      this._coordinates = coordinates;
    },
  },

  persist: true,
});

function isValidAnonymousUserId(uuid: string): uuid is AnonymousUserId {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(uuid);
}

function normalizeCookieConsent(consent: PersistedCookieConsent): CookieConsent | null {
  if (consent === 'accepted') {
    return { analytics: true, marketing: true };
  }

  if (consent === 'rejected') {
    return { analytics: false, marketing: false };
  }

  if (consent === null || typeof consent !== 'object' || typeof consent.analytics !== 'boolean' || typeof consent.marketing !== 'boolean') {
    return null;
  }

  return { analytics: consent.analytics, marketing: consent.marketing };
}
